import { Suspense, useMemo, useState } from 'react';

import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Calendar, MapPin, Pencil } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import {
  type AgentCard_UserFragment,
  Order_By,
  type UpdateListingMutation,
  type UpdateListingMutationVariables,
  type UpdateListingSellersMutation,
  type UpdateListingSellersMutationVariables,
} from '@graphql/__generated__/graphql';
import { AgentCard } from '@modules/_shared/components/AgentCard';
import { CollapsibleContent } from '@modules/_shared/components/CollapsibleContent';
import {
  ELeadCardDisplayMode,
  LeadCard,
} from '@modules/_shared/components/lead/LeadCard';
import { LeadSelect } from '@modules/_shared/components/LeadsSelect';
import { FsboAdvertiserStatus } from '@modules/_shared/components/listing-overview/FsboAdvertiserStatus';
import { FsboBlockDialogProvider } from '@modules/_shared/components/listing-overview/FsboBlockDialogContext';
import {
  ListingBuyerLeadsCard,
  ListingBuyerLeadsCardSkeleton,
} from '@modules/_shared/components/listing-overview/ListingBuyerLeadsCard';
import {
  ListingDevelopmentCard,
  ListingDevelopmentCardSkeleton,
} from '@modules/_shared/components/listing-overview/ListingDevelopmentCard';
import {
  ListingMandateCard,
  ListingMandateCardSkeleton,
} from '@modules/_shared/components/listing-overview/ListingMandateCard';
import { ListingMap } from '@modules/_shared/components/listing-overview/ListingMap';
import {
  ListingOffersCard,
  ListingOffersCardSkeleton,
} from '@modules/_shared/components/listing-overview/ListingOffersCard';
import {
  ListingQuickActions,
  ListingQuickActionsSkeleton,
} from '@modules/_shared/components/listing-overview/ListingQuickActions';
import {
  ListingTransactionsCard,
  ListingTransactionsCardSkeleton,
} from '@modules/_shared/components/listing-overview/ListingTransactionsCard';
import {
  type PropertyCarouselImage,
  PropertyImagesCarousel,
} from '@modules/_shared/components/listing-overview/PropertyImagesCarousel';
import {
  EEditListingForms,
  ListingOverviewEditModal,
} from '@modules/_shared/components/listing-overview-form/ListingOverviewEditModal';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import {
  ListingAgentCard,
  ListingAgentCardSkeleton,
} from '@modules/_shared/components/ListingAgentCard';
import {
  ListingPrice,
  ListingPriceSkeleton,
} from '@modules/_shared/components/ListingPrice';
import MarkdownParser from '@modules/_shared/components/MarkdownParser';
import { PointsOfInterest } from '@modules/_shared/components/PointsOfInterest';
import { PropertyDataListSkeleton } from '@modules/_shared/components/PropertyDataList';
import { PropertyDetails } from '@modules/_shared/components/PropertyDetails';
import { PropertyFeatures } from '@modules/_shared/components/PropertyFeatures';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import {
  SellersActionCard,
  SellersActionCardSkeleton,
} from '@modules/_shared/components/SellersActionCard';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAbbrTimeAgo } from '@modules/_shared/utils/abbr';
import {
  formatAddress,
  formatListingAvailability,
  toHumanReadableDate,
} from '@modules/_shared/utils/formatting';
import { canEditListing } from '@modules/_shared/utils/listingPermissions';
import { evictListingWithoutAccess } from '@modules/listings/evictListingWithoutAccess';
import {
  GET_LISTING_DETAILS,
  GET_LOT,
  type GetListingDetailsData,
  UPDATE_LISTING,
} from '@modules/listings/lotsQueries';
import type { ScrappedListing } from '@modules/reports/cma-reports/step-listing-compare/ListingComparableDetails';
import { useLocale } from '@shared/hooks/locale';

import { RentedRoomDetails } from './components/RentedRoomDetails';

export const LISTING_OVERVIEW_PROPERTY_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingOverviewProperty on properties {
    ...PropertyPoi
    ...PropertyFeatures
    ...PropertyDetails
    ...PropertyFragment

    # Costs
    residential_yearly_rental_income
    residential_potential_rental_income
    commercial_yearly_rental_income
    commercial_potential_rental_income
    income_includes_parking
    is_parking_fully_rented
    parking_yearly_rental_income
    parking_potential_rental_income
    charges
    renovation_fund

    # Energy
    energy_certificate_version
    energy_reference_year
    energy_inspection_date
    estimated_lower_energy_cost
    estimated_higher_energy_cost
    energy_consumption_class
    gas_emissions_class
    consumption
    emissions

    # Misc
    minergie_code
    plot_number

    floor_of_flat
  }
`);

export const LISTING_OVERVIEW_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingOverview on lots {
    id
    title
    description
    created_at
    status
    currency
    sale_price
    offer_type
    price_unit
    rent_net
    rent_extra
    minimum_net_seller_price
    commission_rate
    available_from
    virtual_visit_url
    video_url
    ...AddressOverridesListing
    hide_price_on_portals
    lot_reference
    broker_id
    parking_price
    parking_price_type
    indoor_parking_price
    outdoor_parking_price
    is_parking_included_in_total
    development_id
    broker {
      ...AgentCard_user
      active_teams_users: teams_users(where: { deactivated: { _eq: false } }) {
        team_id
        team {
          id
          lots_member_access
        }
      }
    }
    ...ListedByOverridesListing
    ...ListingPrice
    ...MandateForm
    ...ListingBuyerLeads
    ...ListingQuickActions
    ...ListingOverviewPublishDocumentField
    ...RentedRoomDetails
    sellers(order_by: { seller: { created_at: asc } }) {
      lot_id
      user_id
      seller {
        ...UserCard_user
        fsbo_blocked_at
        fsbo_blocked_reason
        fsbo_block_batch_id
      }
    }
    created_by_user {
      id
      first_name
      last_name
      # The lots trigger and the reviewer also refuse a lot whose CREATOR is
      # blocked; the advertiser card must be able to show that party.
      fsbo_blocked_at
      fsbo_blocked_reason
      fsbo_block_batch_id
    }
    lead {
      ...LeadCard
    }
    property {
      ...ListingOverviewProperty
      ...ListingMap
      property_images(order_by: { order_nr: asc }) {
        id
        property_id
        watermarked_url
        image {
          id
          url
        }
      }
    }
  }
`);

const UPDATE_LISTING_SELLERS = gql(/* GraphQL */ `
  mutation UpdateListingSellers(
    $id: uuid!
    $sellers: [lot_sellers_insert_input!]!
  ) {
    delete_lot_sellers(where: { lot_id: { _eq: $id } }) {
      affected_rows
    }
    insert_lot_sellers(objects: $sellers) {
      returning {
        user_id
        seller {
          ...UserCard_user
        }
      }
    }
  }
`);

const EditableRow: React.FC<
  {
    onEdit?: () => void;
    canEdit?: boolean;
    allowReadOnly?: boolean;
  } & React.PropsWithChildren
> = ({ onEdit, canEdit = true, allowReadOnly = false, children }) => {
  const isInteractive = onEdit != null && (canEdit || allowReadOnly);
  const isReadOnlyInteractive = isInteractive && !canEdit;
  return (
    <Stack
      direction="row"
      spacing={1}
      onClick={isInteractive ? onEdit : undefined}
      onKeyDown={
        isReadOnlyInteractive
          ? event => {
              if (event.target !== event.currentTarget) {
                return;
              }
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onEdit?.();
              }
            }
          : undefined
      }
      role={isReadOnlyInteractive ? 'button' : undefined}
      tabIndex={isReadOnlyInteractive ? 0 : undefined}
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        marginBottom: 1,
        cursor: isInteractive ? 'pointer' : 'default',

        ...(isInteractive
          ? {
              '&:hover': {
                '& .row-edit-icon': {
                  opacity: 1,
                },
              },
            }
          : {}),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      {isInteractive && canEdit && (
        <IconButton
          className="row-edit-icon"
          size="small"
          tabIndex={-1}
          sx={{
            flexShrink: 0,
            color: 'text.secondary',
            backgroundColor: 'action.hover',
            opacity: { xs: 1, md: 0 },
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          <Pencil size={16} />
        </IconButton>
      )}
    </Stack>
  );
};

const ImagesCarouselSkeleton: React.FC = () => {
  const { breakpoints } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('md'));
  const isTablet = useMediaQuery(breakpoints.up('sm'));

  const nbImagesDisplayed = isDesktop ? 3.5 : isTablet ? 2.5 : 1.5;

  return (
    <Stack direction="row" spacing={1} sx={{ maxWidth: '100%' }}>
      {Array.from(new Array(Math.ceil(nbImagesDisplayed))).map((_, index) => (
        <Skeleton key={index} variant="rectangular" width={405} height={270} />
      ))}
    </Stack>
  );
};

type ImagesCarouselType = {
  listing:
    | GetListingDetailsData
    | Pick<ScrappedListing, 'created_at' | 'images'>;
};

export const ImagesCarousel = ({ listing }: ImagesCarouselType) => {
  const { t } = useLocale();

  const timeAgo = getAbbrTimeAgo(listing.created_at, t);
  const firstName =
    'created_by_user' in listing
      ? listing.created_by_user?.first_name
      : undefined;
  const lastName =
    'created_by_user' in listing
      ? listing.created_by_user?.last_name
      : undefined;
  const createdBy =
    firstName != null && lastName != null
      ? `${firstName.charAt(0)}. ${lastName}`
      : null;

  const images: PropertyCarouselImage[] =
    'property' in listing
      ? listing.property.property_images.flatMap((pi, idx) => {
          const url = pi.watermarked_url ?? pi.image?.url;
          if (url == null || url === '') {
            return [];
          }
          return [{ kind: 'url' as const, id: `${pi.id ?? idx}`, url }];
        })
      : listing.images?.flatMap((image, idx) => {
          if (image?.bucket_name == null || image?.file_name == null) {
            return [];
          }
          return [
            {
              kind: 'bucket' as const,
              id: `${idx}`,
              bucketName: image.bucket_name,
              fileName: image.file_name,
            },
          ];
        }) ?? [];

  const topLeftBadge = createdBy != null ? `${timeAgo} - ${createdBy}` : null;
  const topRightBadge =
    'lot_reference' in listing ? listing.lot_reference ?? null : null;

  return (
    <PropertyImagesCarousel
      images={images}
      topLeftBadge={topLeftBadge}
      topRightBadge={topRightBadge}
    />
  );
};

const ListingOverviewSkeleton: React.FC<{
  accordionsOpenedDefault?: boolean;
}> = ({ accordionsOpenedDefault }) => {
  const { t } = useLocale();

  return (
    <>
      <ImagesCarouselSkeleton />
      <Grid
        container
        spacing={{ xs: 0, sm: 2 }}
        sx={{
          px: { xs: 0, sm: 2 },
          py: 2,
          padding: 2,
          alignItems: 'flex-start',
        }}
      >
        <Grid
          size={{
            xs: 12,
            md: 8,
          }}
        >
          <ListingQuickActionsSkeleton />
          <ListingAccordion
            title={t('Edit listing')}
            defaultExpanded={accordionsOpenedDefault}
          >
            <Typography
              variant="h5"
              sx={{
                marginBottom: 2,
              }}
            >
              <Skeleton />
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <MapPin size={18} />
              <Typography variant="body1">
                <Skeleton width={200} />
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <Calendar size={18} />
              <Typography variant="body1">
                <Skeleton width={200} />
              </Typography>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <ListingPriceSkeleton />
            <Box
              sx={{
                marginBottom: 2,
              }}
            >
              <Typography variant="body2">
                <Skeleton width="100%" />
              </Typography>
              <Typography variant="body2">
                <Skeleton width="100%" />
              </Typography>
              <Typography variant="body2">
                <Skeleton width="100%" />
              </Typography>
              <Typography variant="body2">
                <Skeleton width="100%" />
              </Typography>
              <Typography variant="body2">
                <Skeleton width="60%" />
              </Typography>
            </Box>
            <PropertyDataListSkeleton title={t('Property details')} />
            {/* No need to mock more components because it won't be in viewport */}
          </ListingAccordion>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <ListingAgentCardSkeleton openedDefault={accordionsOpenedDefault} />

          <SellersActionCardSkeleton openedDefault={accordionsOpenedDefault} />

          <ListingMandateCardSkeleton openedDefault={accordionsOpenedDefault} />

          <ListingBuyerLeadsCardSkeleton
            openedDefault={accordionsOpenedDefault}
          />

          <ListingOffersCardSkeleton openedDefault={accordionsOpenedDefault} />

          <ListingTransactionsCardSkeleton
            openedDefault={accordionsOpenedDefault}
          />

          <ListingDevelopmentCardSkeleton
            shouldBeOpen={accordionsOpenedDefault}
          />
        </Grid>
      </Grid>
    </>
  );
};

export const ListingOverview = ({
  onAccessLost,
}: {
  onAccessLost?: () => void;
}) => {
  const { t, locale } = useLocale();
  const theme = useTheme();
  const [currentEditForm, setCurrentEditForm] =
    useState<EEditListingForms | null>(null);
  const { lotId } = useParams();
  const { me } = useAppData();
  const apolloClient = useApolloClient();
  const [snackBarDisplayed, setSnackBarDisplayed] = useState(false);
  const { data, error, refetch } = useQuery(GET_LISTING_DETAILS, {
    variables: { id: lotId ?? '' },
    skip: lotId == null,
  });
  const [updateListing] = useMutation<
    UpdateListingMutation,
    UpdateListingMutationVariables
  >(UPDATE_LISTING);
  const [updateListingSellers] = useMutation<
    UpdateListingSellersMutation,
    UpdateListingSellersMutationVariables
  >(UPDATE_LISTING_SELLERS);

  const accordionsOpenedDefault = useMediaQuery(theme.breakpoints.up('sm'));

  const leaveIfListingInaccessible = async () => {
    const { data: lotData, error: lotError } = await apolloClient.query({
      query: GET_LOT,
      variables: { id: lotId ?? '' },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

    if (lotError == null && lotData?.lots_by_pk == null) {
      onAccessLost?.();
    }
  };

  const listing = data?.lots_by_pk;
  const canEdit = useMemo(() => canEditListing(me, listing), [listing, me]);
  const isFsbo = listing?.origin?.name === 'fsbo';

  const listedBy = useMemo<AgentCard_UserFragment | null>(() => {
    if (listing == null) {
      return null;
    }

    if (listing.use_dummy_contact) {
      return {
        id: 'dummy-broker-id',
        full_name: listing.dummy_broker_name,
        // FSBO has no agency — don't render an (empty) organisation block.
        default_team: isFsbo
          ? null
          : {
              id: 'dummy-team-id',
              name: listing.dummy_organisation_name ?? '',
              route: listing.dummy_organisation_route,
              street_number: listing.dummy_organisation_street_number,
              locality: listing.dummy_organisation_locality,
              state: listing.dummy_organisation_state,
              country: listing.dummy_organisation_country_code,
              phone_numbers:
                listing.dummy_organisation_phone != null
                  ? [
                      {
                        id: 'dummy-team-phone-number-id',
                        number: listing.dummy_organisation_phone,
                        primary: true,
                      },
                    ]
                  : [],
            },
        phone_numbers:
          listing.dummy_broker_phone != null
            ? [
                {
                  id: 'dummy-phone-number-id',
                  number: listing.dummy_broker_phone,
                  primary: true,
                },
              ]
            : [],
        user_images: [],
        emails: [],
      };
    }

    // FSBO listings have no broker — the "Listed by" contact is the seller's
    // own profile when the contact override is off. Mirror what IDX publishes:
    // fsbo_hide_last_name shows first name + last initial ("John S."), and
    // fsbo_hide_phone drops the phone.
    if (listing.origin?.name === 'fsbo') {
      const seller = listing.sellers?.[0]?.seller;
      if (seller == null) {
        return null;
      }
      // getUserDisplayName prefers first_name + last_name over full_name, so
      // collapse the last name to its initial there to abbreviate ("John S.").
      const abbreviate =
        listing.fsbo_hide_last_name &&
        seller.first_name != null &&
        seller.last_name != null;
      const lastName = abbreviate
        ? `${seller.last_name!.charAt(0).toUpperCase()}.`
        : seller.last_name;
      return {
        id: seller.id,
        full_name: abbreviate
          ? `${seller.first_name} ${lastName}`
          : seller.full_name,
        first_name: seller.first_name,
        last_name: lastName,
        default_team: null,
        phone_numbers: listing.fsbo_hide_phone ? [] : seller.phone_numbers,
        user_images: seller.user_images,
        emails: seller.emails,
      };
    }

    return listing.broker ?? null;
  }, [listing, isFsbo]);

  const videoFile = listing?.property.property_files.find(
    pFile =>
      pFile.display_on_listing && pFile.file.document_type?.name === 'video',
  );

  const fileToPublish = listing?.property.property_files.find(
    pFile =>
      pFile.display_on_listing && pFile.file.document_type?.name !== 'video',
  );

  return error != null ? (
    <Alert severity="error" sx={{ m: 2 }}>
      <pre>{JSON.stringify(error, null, 2)}</pre>
    </Alert>
  ) : listing == null ? (
    <ListingOverviewSkeleton
      accordionsOpenedDefault={accordionsOpenedDefault}
    />
  ) : (
    <FsboBlockDialogProvider>
      <ImagesCarousel listing={listing} />
      {canEdit === false && (
        <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
          {t(
            'You have read-only access to this listing. Contact a team leader to make changes.',
          )}
        </Alert>
      )}
      <Grid
        container
        spacing={{ xs: 0, sm: 2 }}
        sx={{
          px: { xs: 0, sm: 2 },
          py: 2,
          padding: 2,
          alignItems: 'flex-start',
        }}
      >
        <Grid
          sx={{
            mb: { xs: 1, sm: 0 },
          }}
          size={{
            xs: 12,
            md: 8,
          }}
        >
          <ListingQuickActions listingData={listing} readOnly={!canEdit} />
          <ListingAccordion
            title={t('Edit listing')}
            defaultExpanded={accordionsOpenedDefault}
          >
            <EditableRow
              canEdit={canEdit}
              onEdit={() =>
                setCurrentEditForm(EEditListingForms.TITLE_DESCRIPTION)
              }
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontStyle:
                    listing?.title != null && listing.title.length > 0
                      ? 'normal'
                      : 'italic',
                }}
              >
                {listing.title || t('Add title')}
              </Typography>
            </EditableRow>
            <EditableRow
              canEdit={canEdit}
              onEdit={() => setCurrentEditForm(EEditListingForms.ADDRESS)}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <MapPin size={18} />
                <Typography sx={{ fontSize: '1rem' }}>
                  {formatAddress(listing.property, ', ', true, {
                    hideRoute: listing.address_display_option === 'hide_street',
                    hideStreetNumber:
                      listing.address_display_option === 'hide_street_number',
                    useDummyAddress: listing.use_dummy_address,
                    dummyData: listing,
                  })}
                </Typography>
              </Stack>
            </EditableRow>
            <EditableRow
              canEdit={canEdit}
              onEdit={() => setCurrentEditForm(EEditListingForms.AVAILABILITY)}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Calendar size={18} />
                <Typography sx={{ fontSize: '1rem' }}>
                  {formatListingAvailability(listing.available_from, t)}
                </Typography>
              </Stack>
            </EditableRow>
            <Divider sx={{ my: 2 }} />
            <EditableRow
              canEdit={canEdit}
              allowReadOnly
              onEdit={() => setCurrentEditForm(EEditListingForms.PRICING)}
            >
              <ListingPrice listing={listing} />
            </EditableRow>
            <EditableRow
              canEdit={canEdit}
              onEdit={() =>
                setCurrentEditForm(EEditListingForms.TITLE_DESCRIPTION)
              }
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, my: 1 }}>
                  {t('Description')}
                </Typography>
                {listing.description?.length ? (
                  <CollapsibleContent nbLinesToShow={6}>
                    <Box
                      sx={{
                        typography: 'body2',
                        // Blank lines provide the gaps; an empty paragraph is
                        // exactly one blank line.
                        '& p, ol, ul': {
                          margin: 0,
                        },
                        '& p:empty::before': {
                          content: '"\\00a0"',
                        },
                      }}
                    >
                      <MarkdownParser markdown={listing.description} />
                    </Box>
                  </CollapsibleContent>
                ) : (
                  <em>{t('Add description')}</em>
                )}
              </Box>
            </EditableRow>
            <EditableRow
              canEdit={canEdit}
              onEdit={() => setCurrentEditForm(EEditListingForms.PROPERTY)}
            >
              <PropertyDetails property={listing.property} />
            </EditableRow>
            {listing.offer_type === 'rent' &&
              listing.property.__property_type?.name ===
                'apartment_single_room' && (
                <EditableRow
                  canEdit={canEdit}
                  onEdit={() =>
                    setCurrentEditForm(EEditListingForms.RENTED_ROOM)
                  }
                >
                  <RentedRoomDetails listing={listing} />
                </EditableRow>
              )}
            <EditableRow
              canEdit={canEdit}
              onEdit={() => setCurrentEditForm(EEditListingForms.EQUIPMENT)}
            >
              <PropertyFeatures property={listing.property} />
            </EditableRow>
            <EditableRow
              canEdit={canEdit}
              onEdit={() => setCurrentEditForm(EEditListingForms.POI)}
            >
              <PointsOfInterest property={listing.property} />
            </EditableRow>
            <EditableRow
              canEdit={canEdit}
              onEdit={() => setCurrentEditForm(EEditListingForms.MEDIAS)}
            >
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 0.5, mt: 1 }}
                >
                  {t('Other media')}
                </Typography>
                <List
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      md: 'repeat(2, 1fr)',
                    },
                    columnGap: 5,
                    pt: 0,
                    pb: 0.5,
                  }}
                >
                  <ListItem
                    divider
                    sx={{
                      justifyContent: 'space-between',
                      gap: 2,
                      minWidth: 0,
                    }}
                    disableGutters
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, flexShrink: 0 }}
                    >
                      {t('VR tour')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ...theme.text.ellipsis, textAlign: 'right' }}
                    >
                      {listing?.virtual_visit_url ?? t('None')}
                    </Typography>
                  </ListItem>
                  <ListItem
                    divider
                    sx={{
                      justifyContent: 'space-between',
                      gap: 2,
                      minWidth: 0,
                    }}
                    disableGutters
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, flexShrink: 0 }}
                    >
                      {t('Video')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ...theme.text.ellipsis, textAlign: 'right' }}
                    >
                      {videoFile?.file.name ?? listing?.video_url ?? t('None')}
                    </Typography>
                  </ListItem>
                  <ListItem
                    divider
                    sx={{
                      justifyContent: 'space-between',
                      gap: 2,
                      minWidth: 0,
                    }}
                    disableGutters
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, flexShrink: 0 }}
                    >
                      {t('File to publish')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ...theme.text.ellipsis, textAlign: 'right' }}
                    >
                      {fileToPublish?.file.name ?? t('None')}
                    </Typography>
                  </ListItem>
                </List>
              </Box>
            </EditableRow>
            {listedBy != null && (
              <EditableRow
                canEdit={canEdit}
                onEdit={() => setCurrentEditForm(EEditListingForms.LISTED_BY)}
              >
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                    {t('Listed by')}
                    {isFsbo ? ` (${t('FSBO')})` : ''}
                  </Typography>
                  <AgentCard agent={listedBy} />
                </Box>
              </EditableRow>
            )}
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('Location')}
            </Typography>
            <Box
              sx={{
                mb: 1,
              }}
            >
              {formatAddress(listing.property, null).map((line, index) => (
                <Typography
                  variant="body2"
                  key={index}
                  sx={index === 0 ? { fontWeight: 700 } : {}}
                >
                  {line}
                </Typography>
              ))}
            </Box>
            <ListingMap property={listing.property} />

            <Box
              sx={{
                marginTop: 2,
              }}
            >
              <Typography variant="body2">
                {t('Created {{date}}', {
                  date: toHumanReadableDate(locale, listing.created_at, {
                    hour: 'numeric',
                    minute: 'numeric',
                  }),
                })}
              </Typography>
            </Box>
          </ListingAccordion>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <ListingAgentCard
            openedDefault={accordionsOpenedDefault}
            user={listing.broker}
            secondaryText={listing.broker?.default_team?.name}
            readOnly={!canEdit}
            onUserChanged={async (userId, onError) => {
              const result = await updateListing({
                variables: { id: listing.id, lot: { broker_id: userId } },
                onError,
                update: (cache, { data: mutationResult }) =>
                  evictListingWithoutAccess(
                    cache,
                    mutationResult?.update_lots_by_pk,
                  ),
              });

              if (result.data != null) {
                await leaveIfListingInaccessible();
              }
            }}
            editUserInputProps={{
              orderResultsBy: [{ is_broker: Order_By.Desc }],
            }}
          />

          <SellersActionCard
            openedDefault={accordionsOpenedDefault}
            readOnly={!canEdit}
            sellers={(listing.sellers ?? [])
              .map(({ seller }) => seller)
              .reverse()}
            onSellersChanged={(sellerIds, onError) =>
              updateListingSellers({
                variables: {
                  id: listing.id,
                  sellers: sellerIds.map(id => ({
                    lot_id: listing.id,
                    user_id: id,
                  })),
                },
                onError,
                update: (cache, { data: mutationResult }) => {
                  if (!mutationResult?.insert_lot_sellers) {
                    return;
                  }

                  const newSellers =
                    mutationResult.insert_lot_sellers.returning;

                  cache.modify({
                    id: cache.identify(listing),
                    fields: {
                      sellers() {
                        return newSellers;
                      },
                    },
                  });
                },
              })
            }
          >
            {/* FSBO: the advertiser's standing (blocked / linked accounts)
                and the block button live with the seller they are about.
                Tenant admins only, decided here so the card's spacer is not
                rendered around a component that would return null. */}
            {isFsbo && me?.is_admin === true && (
              <FsboAdvertiserStatus
                lotId={listing.id}
                // The block is enforced on the creator or ANY seller (lots
                // trigger, reviewer gate), so the card follows the blocked
                // party wherever it sits, then falls back to the first
                // seller, then to the creator: an FSBO lot can carry no
                // seller row at all, and it must still be blockable here.
                advertiser={
                  listing.sellers?.find(s => s.seller?.fsbo_blocked_at != null)
                    ?.seller ??
                  (listing.created_by_user?.fsbo_blocked_at != null
                    ? listing.created_by_user
                    : null) ??
                  listing.sellers?.[0]?.seller ??
                  listing.created_by_user ??
                  null
                }
                readOnly={!canEdit}
              />
            )}
          </SellersActionCard>

          <ListingMandateCard
            openedDefault={accordionsOpenedDefault}
            listing={listing}
            onEdit={
              canEdit
                ? () => setCurrentEditForm(EEditListingForms.MANDATE)
                : undefined
            }
          />

          <ListingBuyerLeadsCard
            openedDefault={accordionsOpenedDefault}
            listingId={lotId}
            buyerLeadsData={listing}
            readOnly={!canEdit}
          />

          <ListingOffersCard
            openedDefault={accordionsOpenedDefault}
            listingId={lotId}
            readOnly={!canEdit}
          />

          <ListingTransactionsCard
            listingId={lotId}
            openedDefault={accordionsOpenedDefault}
            readOnly={!canEdit}
          />

          <ListingAccordion
            title={t('Lead')}
            defaultExpanded={accordionsOpenedDefault}
          >
            {listing.lead != null ? (
              <LeadCard
                lead={listing.lead}
                displayMode={ELeadCardDisplayMode.COMPACT}
                openOnNewTab
                onLeadChanged={
                  canEdit
                    ? (leadId, onError) =>
                        updateListing({
                          variables: {
                            id: listing.id,
                            lot: { lead_id: leadId },
                          },
                          onError,
                        })
                    : undefined
                }
              />
            ) : canEdit ? (
              <LeadSelect
                placeholder={t('Associate a lead')}
                onChange={lead =>
                  updateListing({
                    variables: { id: listing.id, lot: { lead_id: lead?.id } },
                  })
                }
              />
            ) : (
              <Typography variant="body2">{t('No lead associated')}</Typography>
            )}
          </ListingAccordion>

          <ListingDevelopmentCard
            shouldBeOpen={accordionsOpenedDefault}
            listingId={lotId!}
            developmentId={listing.development_id}
            readOnly={!canEdit}
          />
        </Grid>
      </Grid>
      <Suspense fallback={null}>
        <ListingOverviewEditModal
          listing={listing}
          opened={
            currentEditForm != null &&
            (canEdit || currentEditForm === EEditListingForms.PRICING)
          }
          editForm={currentEditForm}
          canEdit={canEdit}
          onClose={() => setCurrentEditForm(null)}
          onSuccess={() => setSnackBarDisplayed(true)}
          onListedBySaved={leaveIfListingInaccessible}
          onAppraised={refetch}
        />
      </Suspense>
      {snackBarDisplayed && (
        <RASnackbar
          message={t('Your changes were saved successfully!')}
          onClose={() => setSnackBarDisplayed(false)}
          severity="success"
        />
      )}
    </FsboBlockDialogProvider>
  );
};

export default ListingOverview;
